/**
 * The workspace's single advance affordance — a sticky strip under the
 * identity header carrying: progress segments (one per section), the
 * current stage's blocker chips (deep-linked to the owning section), ONE
 * primary action, and an overflow menu for Revert…/Reject….
 *
 * Blockers come from the read-only /gates endpoint; the advance/start
 * 409s remain the enforcement of record. `severity: "info"` chips never
 * disable the primary.
 */

import { ReactNode } from 'react';

import { ActionIcon, Badge, Button, Card, Group, Loader, Menu, Text, Tooltip } from '@mantine/core';
import {
  IconArrowBackUp,
  IconBan,
  IconCheck,
  IconDotsVertical,
  IconInfoCircle,
} from '@tabler/icons-react';

import type {
  TriageGateBlocker,
  TriageSectionSlug,
  WorkspaceSection,
} from '@/types/report-request';
import classes from './TriageWorkspace.module.css';

export interface GateStripPrimary {
  label: string;
  onClick: () => void | Promise<void>;
  disabled?: boolean;
  loading?: boolean;
}

interface GateStripProps {
  sections: WorkspaceSection[];
  /** Section owning the current stage; null for record states. */
  currentSlug: TriageSectionSlug | null;
  /** True when every section is behind us (started). */
  allComplete?: boolean;
  blockers: TriageGateBlocker[];
  /** Inline busy note (e.g. "Extracting subjects…") shown next to the primary. */
  busyLabel?: string | null;
  /** Status-only text for record states (started/rejected) — replaces the primary. */
  statusChip?: ReactNode;
  primary?: GateStripPrimary | null;
  onBlockerClick: (blocker: TriageGateBlocker) => void;
  onRevert?: () => void;
  onReject?: () => void;
}

export function GateStrip({
  sections,
  currentSlug,
  allComplete = false,
  blockers,
  busyLabel,
  statusChip,
  primary,
  onBlockerClick,
  onRevert,
  onReject,
}: Readonly<GateStripProps>) {
  const currentIdx = sections.findIndex((s) => s.slug === currentSlug);

  return (
    <Card
      shadow="sm"
      padding="xs"
      radius="md"
      className={classes.gateStrip}
      data-testid="gate-strip"
    >
      <Group justify="space-between" wrap="nowrap" gap="sm">
        <Group gap={4} wrap="nowrap" className={classes.segments}>
          {sections.map((section, idx) => {
            const state = allComplete
              ? 'completed'
              : currentIdx === -1
                ? 'upcoming'
                : idx < currentIdx
                  ? 'completed'
                  : idx === currentIdx
                    ? 'active'
                    : 'upcoming';
            return (
              <Group key={section.slug} gap={4} wrap="nowrap">
                {idx > 0 && <div className={classes.segmentDivider} />}
                <Badge
                  size="sm"
                  radius="sm"
                  variant={state === 'active' ? 'filled' : 'light'}
                  color={state === 'completed' ? 'green' : state === 'active' ? 'blue' : 'gray'}
                  leftSection={state === 'completed' ? <IconCheck size={12} /> : undefined}
                  data-testid={`gate-segment-${section.slug}`}
                >
                  {section.label}
                </Badge>
              </Group>
            );
          })}
        </Group>

        <Group gap={6} wrap="wrap" justify="flex-end" style={{ flex: 1 }}>
          {blockers.map((b) => (
            <BlockerChip
              key={`${b.code}-${JSON.stringify(b.refs)}`}
              blocker={b}
              onClick={onBlockerClick}
            />
          ))}
          {busyLabel && (
            <Group gap={6} wrap="nowrap">
              <Loader size="xs" />
              <Text size="xs" c="dimmed">
                {busyLabel}
              </Text>
            </Group>
          )}
          {statusChip}
          {primary && (
            <Button
              size="xs"
              onClick={() => void primary.onClick()}
              disabled={primary.disabled}
              loading={primary.loading}
              data-testid="gate-primary"
            >
              {primary.label}
            </Button>
          )}
          {(onRevert || onReject) && (
            <Menu position="bottom-end" withinPortal>
              <Menu.Target>
                <ActionIcon variant="subtle" color="gray" aria-label="More actions">
                  <IconDotsVertical size={16} />
                </ActionIcon>
              </Menu.Target>
              <Menu.Dropdown>
                {onRevert && (
                  <Menu.Item leftSection={<IconArrowBackUp size={14} />} onClick={onRevert}>
                    Revert…
                  </Menu.Item>
                )}
                {onReject && (
                  <Menu.Item color="red" leftSection={<IconBan size={14} />} onClick={onReject}>
                    Reject…
                  </Menu.Item>
                )}
              </Menu.Dropdown>
            </Menu>
          )}
        </Group>
      </Group>
    </Card>
  );
}

function BlockerChip({
  blocker,
  onClick,
}: Readonly<{ blocker: TriageGateBlocker; onClick: (b: TriageGateBlocker) => void }>) {
  const info = blocker.severity === 'info';
  const working = blocker.code === 'ai_working';
  const chip = (
    <Badge
      size="sm"
      radius="xl"
      variant={info ? 'outline' : 'light'}
      color={info ? 'gray' : working ? 'blue' : 'orange'}
      leftSection={
        working ? (
          <Loader size={10} color="blue" />
        ) : info ? (
          <IconInfoCircle size={12} />
        ) : undefined
      }
      component="button"
      type="button"
      onClick={() => onClick(blocker)}
      className={classes.blockerChip}
      data-testid={`gate-blocker-${blocker.code}`}
    >
      {blocker.label}
    </Badge>
  );
  return <Tooltip label="Jump to the section that fixes this">{chip}</Tooltip>;
}
